import { asArray } from 'ol/color';
import { Circle as CircleStyle, Stroke, Style } from 'ol/style';

/**
 * Values and styles for the "Find similar" overlay. Tiles carry the encoded similarity (0–255) in
 * the red band, which WebGL reads as a 0–1 "band value". Above the threshold, the theme's
 * `similarityColormap` runs from the threshold to the most similar, and the overlay grows more
 * opaque with it, so near-threshold matches show without hiding the imagery.
 */

export const DEFAULT_OPACITY = 0.75;

// Overlay alpha at the threshold and at the most similar, before the opacity slider
const RAMP_ALPHA = { from: 0.45, to: 0.9 };

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

/**
 * @param {number} similarity
 * @param {{lo: number, hi: number}} valueRange - From the exemplar
 * @returns {number} Band value, 0–1
 */
export const similarityToBand = (similarity, { lo, hi }) =>
	hi === lo ? 0 : clamp((similarity - lo) / (hi - lo), 0, 1);

/**
 * One step of the encoding, the slider's step.
 * @param {{lo: number, hi: number}} valueRange
 * @returns {number}
 */
export const thresholdStep = ({ lo, hi }) => (hi - lo) / 255 || 0.01;

/**
 * The threshold to start at: the middle of the exemplar's value range, on a slider step. The
 * range differs per exemplar and orthophoto (e.g. −0.07 to 0.56), so no fixed value works.
 * @param {{lo: number, hi: number}} valueRange
 * @returns {number}
 */
export const defaultThreshold = valueRange => {
	const step = thresholdStep(valueRange);
	const middle = (valueRange.hi - valueRange.lo) / 2;
	return valueRange.lo + Math.round(middle / step) * step;
};

/**
 * Where a similarity sits on the colour ramp: 0 at the threshold, 1 at the most similar.
 * @param {number} similarity
 * @param {number} threshold - Similarity
 * @param {{hi: number}} valueRange - From the exemplar
 * @returns {number} 0–1
 */
export const rampPosition = (similarity, threshold, { hi }) =>
	hi <= threshold ? 1 : clamp((similarity - threshold) / (hi - threshold), 0, 1);

// [position 0–1, [r, g, b]] for each colour, evenly spaced
const stops = colors => colors.map((color, i) => [i / (colors.length - 1), asArray(color)]);

/**
 * The ramp as [position, [r, g, b, alpha]] stops, with the alpha rise built in.
 * @param {string[]} colors - The theme's `similarityColormap`
 * @returns {Array}
 */
export const rampStops = colors =>
	stops(colors).map(([position, [r, g, b]]) => [
		position,
		[r, g, b, RAMP_ALPHA.from + (RAMP_ALPHA.to - RAMP_ALPHA.from) * position],
	]);

/**
 * The colour at a ramp position, for the list and the suggestion rings.
 * @param {string[]} colors - The theme's `similarityColormap`
 * @param {number} rampAt - 0–1, from rampPosition
 * @returns {string} CSS colour
 */
export const colormapColor = (colors, rampAt) => {
	const position = clamp(rampAt, 0, 1) * (colors.length - 1);
	const lower = Math.floor(position);
	const upper = Math.min(lower + 1, colors.length - 1);
	const [a, b] = [asArray(colors[lower]), asArray(colors[upper])];
	const mixed = [0, 1, 2].map(i => Math.round(a[i] + (b[i] - a[i]) * (position - lower)));
	return `rgb(${mixed.join(', ')})`;
};

/**
 * A colour at another alpha, for fills and faded outlines.
 * @param {string} color - Any CSS colour OpenLayers can parse
 * @param {number} alpha - 0–1
 * @returns {string} CSS colour
 */
export const withAlpha = (color, alpha) => {
	const [r, g, b] = asArray(color);
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * @param {string[]} colors - The theme's `similarityColormap`
 * @returns {string} CSS gradient from left to right, for the legend
 */
export const colormapGradient = colors => `linear-gradient(to right, ${colors.join(', ')})`;

/**
 * Style for the WebGL tile layer: transparent outside the orthophoto and below the threshold, the
 * colour ramp above it. Threshold and opacity are style variables, so a slider only calls
 * `layer.updateStyleVariables` and never refetches tiles.
 * @param {Object} params
 * @param {string[]} params.colors - The theme's `similarityColormap`
 * @param {number} params.threshold - Band value, 0–1
 * @param {number} params.opacity - 0–1
 * @returns {Object} WebGLTile style
 */
export const similarityLayerStyle = ({ colors, threshold, opacity }) => {
	// 0 at the threshold, 1 at the most similar
	const rampAt = [
		'clamp',
		[
			'/',
			['-', ['band', 1], ['var', 'threshold']],
			['clamp', ['-', 1, ['var', 'threshold']], 0.001, 1],
		],
		0,
		1,
	];
	const channel = i => [
		'interpolate',
		['linear'],
		rampAt,
		...stops(colors).flatMap(([position, rgb]) => [position, rgb[i]]),
	];
	const alpha = [
		'*',
		['+', RAMP_ALPHA.from, ['*', RAMP_ALPHA.to - RAMP_ALPHA.from, rampAt]],
		['var', 'opacity'],
	];
	return {
		variables: { threshold, opacity },
		color: [
			'case',
			// Alpha is 0 outside the orthophoto; edges are interpolated, so compare with 0.5
			['<', ['band', 4], 0.5],
			[0, 0, 0, 0],
			['<', ['band', 1], ['var', 'threshold']],
			[0, 0, 0, 0],
			['color', channel(0), channel(1), channel(2), alpha],
		],
	};
};

/**
 * A suggestion: a ring in its similarity's colour, with a halo when selected.
 * @param {Object} params
 * @param {string[]} params.colors - The theme's `similarityColormap`
 * @param {string} params.halo - Colour around the selected suggestion
 * @param {number} params.rampAt - The suggestion on the colour ramp, from rampPosition
 * @param {boolean} params.selected
 * @returns {Style[]}
 */
export const suggestionStyle = ({ colors, halo, rampAt, selected }) => [
	...(selected
		? [
				new Style({
					image: new CircleStyle({
						radius: 13,
						stroke: new Stroke({ color: halo, width: 3 }),
					}),
				}),
			]
		: []),
	new Style({
		image: new CircleStyle({
			radius: 8,
			stroke: new Stroke({ color: colormapColor(colors, rampAt), width: 3 }),
		}),
	}),
];
